/**
 * DOCUMENTATION:
 * Component: CssResponsiveArchitecture
 * Purpose: Educational page detailing modern responsive techniques and the BEM CSS architecture.
 * * * Note for Junior Developers:
 * - "Mobile-First" isn't just a buzzword; it's a technical strategy. Write your default CSS for mobile screens,
 * and only use `@media (min-width)` to add complexity as the screen gets larger. It's much easier to scale UP than to scale DOWN.
 * - BEM (Block, Element, Modifier) solves the biggest problem in CSS: naming things. It ensures your classes are
 * highly specific, readable, and perfectly scoped without relying on deep HTML nesting.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssResponsiveArchitecture() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Architecture Concepts
    const architectureConcepts = useMemo(() => [
        { concept: "Mobile-First (@media)", purpose: "Responsive Strategy", description: "Default CSS targets mobile devices. Media queries with `min-width` are used to add complexity for larger screens." },
        { concept: "BEM: Block", purpose: "Standalone Entity", description: "A meaningful, independent component (e.g., `card`, `header`, `btn`)." },
        { concept: "BEM: Element", purpose: "Component Part", description: "A part of a block that has no standalone meaning, denoted by two underscores (e.g., `card__image`, `card__title`)." },
        { concept: "BEM: Modifier", purpose: "State / Variant", description: "A flag on a block or element to change its appearance or behavior, denoted by two hyphens (e.g., `btn--primary`, `card--dark`)." },
        { concept: "Fluid Units (clamp)", purpose: "Dynamic Scaling", description: "A modern CSS function that takes a minimum, preferred, and maximum value. It replaces dozens of media queries." },
    ], []);

    // Coding examples demonstrating BEM and Responsiveness
    const codingModules = useMemo(() => [
        {
            title: "1. The BEM Methodology",
            code: `\n<div class="card">\n  <h2 class="title">Product</h2>\n  <button class="active">Buy</button>\n</div>\n\n\n<div class="product-card">\n  <h2 class="product-card__title">Laptop</h2>\n  <p class="product-card__desc">High performance.</p>\n  \n  <button class="btn btn--primary">Buy Now</button>\n</div>`,
            desc: "BEM flattens your CSS specificity. Because every class name is unique (e.g., `.product-card__title`), you never have to deeply nest selectors in your stylesheet to override generic names like `.title`."
        },
        {
            title: "2. BEM with SCSS (The Perfect Match)",
            code: `// SCSS allows us to generate BEM classes rapidly using the '&' symbol\n.product-card {\n  background-color: white;\n  border-radius: 8px;\n\n  // Compiles to: .product-card__title\n  &__title {\n    font-size: 1.5rem;\n    color: #333;\n  }\n\n  // Compiles to: .product-card--dark\n  &--dark {\n    background-color: #222;\n    color: white;\n  }\n}`,
            desc: "This is where SCSS shines. By appending the `__` (Element) or `--` (Modifier) to the parent `&`, you write less code while still outputting strictly scoped BEM architecture."
        },
        {
            title: "3. Mobile-First Media Queries & clamp()",
            code: `/* 1. Mobile-First Layout Default */\n.grid-layout {\n  display: grid;\n  grid-template-columns: 1fr; /* 1 column on mobile */\n}\n\n/* 2. Scale UP for Tablets and larger */\n@media (min-width: 768px) {\n  .grid-layout {\n    grid-template-columns: 1fr 1fr; /* 2 columns */\n  }\n}\n\n/* 3. Fluid Typography with clamp() */\n.hero-title {\n  /* \n    min-size: 2rem (mobile)\n    preferred: 5vw (scales with viewport)\n    max-size: 4rem (desktop cap)\n  */\n  font-size: clamp(2rem, 5vw, 4rem);\n}`,
            desc: "Instead of writing 5 different media queries to shrink a font size as the screen gets smaller, `clamp()` calculates it mathematically on the fly. It is a massive time-saver for responsive typography and padding."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    RESPONSIVE ARCHITECTURE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Designing for Every Device
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A modern web application must look flawless on a 300px smartwatch and a 4K television. Achieving this requires a <strong>Mobile-First</strong> mindset, fluid units, and a strict CSS naming architecture like <strong>BEM</strong> to prevent your stylesheets from collapsing into a chaotic mess of <code>!important</code> overrides as your project grows.
                        </Typography>

                        {/* ✅ Architecture Concepts Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Core Architecture Rules</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Concept</div>
                                    <div>Primary Purpose</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {architectureConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
